import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useContext
} from 'react';
import { graphql, useStaticQuery, Link as GatsbyLink } from 'gatsby';
import dataLayerPush from '@helpers/dataLayerPush';

import {
  Box,
  Flex,
  Link,
  useDisclosure,
  Image,
  HStack
} from '@chakra-ui/react';
import LocaleOptions from '@components/molecules/LocaleOptions';
import Burger from '@components/atoms/Burger';
import { LocaleContext } from '@helpers/LocaleProvider';
import logo from '@assets/images/milka-logo.svg';

import { probablyExternal } from '@helpers/linkHelpers';

export const height = { base: `66px`, md: `147px` };

const Header = ({ headerMenu }) => {
  const {
    site: {
      siteMetadata: { title: siteTitle }
    }
  } = useStaticQuery(graphql`
    query {
      site {
        siteMetadata {
          title
        }
      }
    }
  `);

  // Sets the current locale .
  const { locale } = useContext(LocaleContext);

  const { isOpen, onToggle } = useDisclosure();
  const [isScrollingDown, setIsScrollingDown] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [currentNavItemIndex, setCurrentNavItemIndex] = useState(-1);

  const navItemsRef = useRef([]);

  const showNav = !isScrollingDown;

  const handleClick = useCallback((eventName, url, title, navLevel) => {
    dataLayerPush({
      event: eventName,
      interaction: {
        click_text: title,
        link_url: url,
        nav_level: navLevel || 1
      }
    });
  }, []);

  // Determines when the user is scrolling down to set visibility of header.
  useEffect(() => {
    let oldScroll = window.scrollY;
    let nextThreshold = 0;

    const onScroll = () => {
      const newScroll = window.scrollY;
      const atTop = newScroll <= 0;
      const scrollingDown = oldScroll < newScroll;

      setIsScrollingDown(
        !atTop &&
          (scrollingDown || (!scrollingDown && newScroll > nextThreshold))
      );

      if (atTop === true || scrollingDown === true) {
        nextThreshold = newScroll - window.innerHeight * 0.15;
      }

      oldScroll = newScroll;

      setIsAtTop(atTop);
    };

    window.addEventListener(`scroll`, onScroll, { passive: true });

    return () =>
      window.removeEventListener(`scroll`, onScroll, { passive: true });
  }, []);

  useEffect(() => {
    /**
     * onKeyDown method for the header
     * @param {KeyboardEvent} event onKeyDown event
     */
    const onKeyDown = ({ key }) => {
      const menuItems = headerMenu.nodes[0].attributes.items.data.map(
        (item, index) => index
      );

      if (typeof key === `undefined`) {
        return;
      }

      if (key.toLowerCase() === `arrowleft`) {
        const previousIndex = currentNavItemIndex - 1;
        const previousItem =
          previousIndex < 0 ? menuItems.length - 1 : previousIndex;

        if (navItemsRef.current && navItemsRef.current.length > 0) {
          const currentElement = navItemsRef.current[previousItem];
          currentElement.focus();
        }

        setCurrentNavItemIndex(previousItem);
      }

      if (key.toLowerCase() === `arrowright`) {
        const nextIndex = currentNavItemIndex + 1;
        const nextItem = nextIndex > menuItems.length - 1 ? 0 : nextIndex;

        if (navItemsRef.current && navItemsRef.current.length > 0) {
          const currentElement = navItemsRef.current[nextItem];
          currentElement.focus();
        }

        setCurrentNavItemIndex(nextItem);
      }
    };

    window.addEventListener(`keydown`, onKeyDown);

    return () => window.removeEventListener(`keydown`, onKeyDown);
  }, [
    currentNavItemIndex,
    headerMenu.nodes,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    headerMenu.nodes[0].attributes.items.data
  ]);

  return headerMenu?.nodes[0].attributes === undefined ||
    headerMenu.nodes[0].attributes?.items?.data?.length <= 0 ? (
    // eslint-disable-next-line react/jsx-indent
    <Box color="red">Menu render error</Box>
  ) : (
    <Box position="relative" zIndex="100">
      <Flex w="100%" justifyContent="center">
        <Flex
          w="100vw"
          h={{ base: height.base, md: height.md }}
          alignItems="center"
          justifyContent="space-between"
          px={{ base: 6, lg: 14 }}
          position="fixed"
          bg={isAtTop ? `transparent` : `milka.primary.dark-purple`}
          top={showNav || isOpen ? `0px` : `-${height.md}`}
          zIndex="2"
          transition="top 0.5s ease-in-out, background-color .3s .3s ease-in-out">
          <Flex
            to={`/${locale}`}
            as={GatsbyLink}
            position="relative"
            width={{ base: `5.676rem`, md: `8.375rem` }}
            mr={{ base: `0`, xl: `4` }}
            zIndex="2"
            transition="transform .5s ease-in-out"
            _hover={{
              transform: `scale(1.1)`
            }}
            onClick={
              () => handleClick(`nav_logo_click`, `/`, `logo`, `logo`)
              // eslint-disable-next-line react/jsx-curly-newline
            }>
            <Image src={logo} alt={siteTitle} />
          </Flex>
          <Flex
            as="nav"
            role="navigation"
            position={{ base: `fixed`, md: `static` }}
            top={{ base: `0`, md: `auto` }}
            right={{ base: `0`, md: `auto` }}
            left={{ base: `0`, md: `auto` }}
            bottom={{ base: `0`, md: `auto` }}
            display="flex"
            flexDirection={{ base: `column`, md: `row` }}
            alignItems="center"
            backgroundColor={{
              base: `milka.primary.dark-purple`,
              md: `transparent`
            }}
            py={{ base: `4.125rem`, md: 0 }}
            ml="auto"
            overflow={{ base: `auto`, md: `initial` }}
            transform="auto"
            translateY={isOpen ? `0` : { base: `-100%`, md: `0` }}
            transition=".3s ease-in-out">
            <HStack
              as="ul"
              px={{ base: 6, md: 0 }}
              flexDirection={{ base: `column`, md: `row` }}
              listStyleType="none"
              spacing={{ base: 6, lg: 14 }}
              pt={{ base: 10, md: 0 }}
              mr={{ md: 4, lg: 14 }}>
              {headerMenu.nodes[0].attributes.items.data.map(
                ({ attributes: primaryItem }) => (
                  <Box as="li" key={primaryItem.title}>
                    <Link
                      href={
                        probablyExternal(primaryItem.url) === true
                          ? `${primaryItem.url}`
                          : `/${locale}${primaryItem.url}`
                      }
                      target={
                        typeof primaryItem.target !== `undefined` &&
                        primaryItem.target !== null
                          ? primaryItem.target
                          : `_self`
                      }
                      display="block"
                      fontFamily="headingSub"
                      fontWeight="bold"
                      fontSize={{ base: `3xl`, md: `xl` }}
                      letterSpacing={{ base: null, md: `-0.0156rem` }}
                      lineHeight="1.2"
                      textTransform="uppercase"
                      textDecoration="none"
                      transition=".3s ease-in-out"
                      _hover={{
                        transform: `auto`,
                        scale: `1.1`
                      }}>
                      {primaryItem.title}
                    </Link>
                  </Box>
                )
              )}
            </HStack>
            <Box
              width={{ base: `100%`, md: `auto` }}
              minWidth={{ md: `6.75rem` }}
              mt={{ base: 6, md: -1 }}>
              <LocaleOptions />
            </Box>
          </Flex>
          <Flex
            display={{ base: `block`, md: `none` }}
            transform="auto"
            rotate={isOpen ? `90deg` : 0}
            transition={isOpen ? `transform .2s  ease-in-out` : null}>
            <Burger
              isOpen={isOpen}
              onClick={onToggle}
              color="#fff"
              zIndex="2"
              cursor="pointer"
            />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Header;
